import React, {useState} from 'react';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";

import {StyledBoxWrapper} from "../../DigitizationDetails.styled";
import SimpleLinkButton from "../../../../../components/documents-digitization/SimpleLinkButton";
import {useTranslation} from "react-i18next";
import {BlueDot} from "./DigitizationFileActivityLog.styled";
import CloseIcon from "@mui/icons-material/Close";
import {CancelButton} from "../../../../../components/common/ActionButtons";
import {formatDateWithLanguage} from "../../../../../utils/format-date";
import {LocalDocumentHistoryResponse} from "../../../../../models/local-documents.models";

interface DigitizationFileActivityLogProps {
    fileName: string;
    history: LocalDocumentHistoryResponse[] | null;
}

const DigitizationFileActivityLog = ({fileName, history}: DigitizationFileActivityLogProps) => {
    const {t} = useTranslation();

    const [openDialog, setOpenDialog] = useState(false);

    const hasMoreLogs = history && history.length > 5;
    const availableHistory = hasMoreLogs ? history.slice(0, 5) : history;

    return (
        <StyledBoxWrapper>
            <Typography variant="h6" fontWeight={500} mb={2}>
                {t('digitizationDetails.activityLog.title')}
            </Typography>

            {(!history || !history.length) &&
                <Stack>
                    <Typography variant={"body2"} color="text.secondary">
                        {t('digitizationDetails.activityLog.noActivityInfo')}
                    </Typography>
                </Stack>
            }

            {availableHistory && availableHistory?.map((item) => (
                <Stack key={item.id} direction="row" alignItems="flex-start" ml={1} mb={2}>
                    <BlueDot />

                    <Stack direction="column" ml={2} minWidth={0}>
                        <Typography
                            variant={"body1"}
                            color="text.primary"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.operationName}
                        </Typography>

                        <Typography variant={"body2"} color="secondary.light" mt={0.5}>
                            <span>{item.performedAt ? formatDateWithLanguage(item.performedAt) : '\u2014'}</span>
                            <span>{item.performedBy
                                    ? ('\n' + t('digitizationDetails.activityLog.performedBy') + '\n' + item.performedBy)
                                    : '\u2014'}
                            </span>
                        </Typography>
                    </Stack>
                </Stack>
            ))}

            {hasMoreLogs &&
                <Box display="flex">
                    <SimpleLinkButton onClick={() => setOpenDialog(true)}>
                        {t('digitizationDetails.activityLog.showMoreLogsButton')}
                    </SimpleLinkButton>
                </Box>
            }

            {hasMoreLogs && openDialog &&
                <ActivityLogHistoryDialog open={openDialog} history={history} fileName={fileName} onClose={() => setOpenDialog(false)} />
            }
        </StyledBoxWrapper>
    );
};


const ActivityLogHistoryDialog = ({ open, history, fileName, onClose }: { open: boolean; history: LocalDocumentHistoryResponse[]; fileName: string; onClose: () => void }) => {
    const {t} = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{ overflow: 'auto', height: '100%'}}
            aria-labelledby="change-assigned-patient-dialog-title"
            aria-modal="true"
        >
            <DialogTitle
                id="change-assigned-patient-dialog-title"
                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
            >
                {t('digitizationDetails.activityLog.title')}

                <Tooltip title={t('common.close')}>
                    <IconButton onClick={onClose}
                                size="small"
                                aria-label={t('common.close') as string}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent>
                <Typography variant='body2' color="secondary.light" mt={1} mb={2}>
                    {fileName}
                </Typography>

                {(history && history.length > 0) && history.map((item) => (
                    <Stack key={item.id} direction="row" alignItems="flex-start" mb={2}>
                        <BlueDot />

                        <Stack direction="column" ml={2}>
                            <Typography variant={"body1"} color="primary">
                                {item.operationName}
                            </Typography>

                            <Typography variant={"body2"} color="secondary.light" mt={0.5}>
                                <span>{item.performedAt ? formatDateWithLanguage(item.performedAt) : '\u2014'}</span>
                                <span>{item.performedBy
                                    ? ('\n' + t('digitizationDetails.activityLog.performedBy') + '\n' + item.performedBy)
                                    : '\u2014'}
                                </span>
                            </Typography>
                        </Stack>
                    </Stack>
                ))}
            </DialogContent>

            <DialogActions>
                <CancelButton onClick={onClose}>
                    {t('common.cancel')}
                </CancelButton>
            </DialogActions>
        </Dialog>
    );
};

export default DigitizationFileActivityLog;